/**
 * Seed currency constants - SINGLE SOURCE OF TRUTH
 * Safe for client and server. Used for UI display, API logic, add/subtract seeds.
 *
 * Import from here everywhere:
 * - lib/services/seeds-service.ts (re-exports for API usage)
 * - app/(app)/seeds/ (Earn Seeds, Seed Costs sections)
 * - components (SeedsCost, TierSelector, etc.)
 *
 * Seed cost formula (100% profit target):
 * - 1 seed = $0.04 to the user (SEED_PRICE_USD).
 * - Target 100% profit => revenue = 2 × AI cost (TARGET_PROFIT_MULTIPLIER = 2).
 * - So: seeds = ceil((AI_cost_USD × 2) / 0.04). Where we have an AI cost estimate
 *   we use this; otherwise the value in SEED_COSTS is the single source of truth
 *   for gating and display.
 */

/**
 * Seed pricing baseline used for seed-cost calculations that target a fixed
 * margin from estimated AI spend.
 */
export const SEED_PRICE_USD = 0.04;
export const TARGET_PROFIT_MULTIPLIER = 2; // 100% profit => revenue = 2x cost

/**
 * Why autopsy AI cost estimate:
 * - Video autopsy Gemini usage is estimated at ~$0.012 per completed run.
 * - With 100% profit target and $0.04/seed:
 *   required revenue = 0.012 * 2 = 0.024
 *   seeds = ceil(0.024 / 0.04) = 1
 */
export const WHY_ANALYSIS_AI_COST_USD = 0.012;
export const WHY_ANALYSIS_SEED_COST = Math.max(
  1,
  Math.ceil(
    (WHY_ANALYSIS_AI_COST_USD * TARGET_PROFIT_MULTIPLIER) / SEED_PRICE_USD
  )
);

/**
 * Intelligence analysis AI cost estimate:
 * - Full intelligence run (~18 calls) is estimated at ~$0.025 per completed run.
 * - With 100% profit target and $0.04/seed:
 *   required revenue = 0.025 * 2 = 0.05
 *   seeds = ceil(0.05 / 0.04) = 2
 */
export const INTELLIGENCE_ANALYSIS_AI_COST_USD = 0.025;
export const INTELLIGENCE_ANALYSIS_SEED_COST = Math.max(
  1,
  Math.ceil(
    (INTELLIGENCE_ANALYSIS_AI_COST_USD * TARGET_PROFIT_MULTIPLIER) /
      SEED_PRICE_USD
  )
);

/**
 * Precision Trim analysis AI cost estimate (one run = transcription + plan):
 * - ElevenLabs Scribe: ~$0.0067/min → ~$0.10 for 15 min average.
 * - Grok plan generation: ~$0.01–0.02.
 * - Total ~$0.12 per analysis. With 100% profit and $0.04/seed:
 *   required revenue = 0.12 * 2 = 0.24
 *   seeds = ceil(0.24 / 0.04) = 6
 */
export const PRECISION_TRIM_ANALYSIS_AI_COST_USD = 0.12;

/**
 * Diagnose (ads) AI cost estimates:
 * - Short video (≤15s): flash-lite ~$0.001 per run (24 frames).
 * - Long video / hybrid (>15s or hybrid content): gemini-3.1-pro ~$0.012 per run
 *   (2fps dense sampling: 30–120 frames at 768px/75% JPEG).
 * - Synthetic ad with native video upload (Files API → gemini-3.1-pro):
 *   Pro input pricing ($1.25/1M tok) × ~290 tokens/sec of native video+audio
 *   plus ~3K tokens of structured-JSON output ($10/1M tok). Worst case at our
 *   25MB inline cap (~90s vertical short with a maxed-out defect-log payload)
 *   lands around $0.065. We budget for that worst case so the formula keeps
 *   ≥100% margin on every synthetic ad.
 * - With 100% profit target and $0.04/seed:
 *   Short:        ceil(0.001 * 2 / 0.04) = ceil(0.05)  = 1
 *   Long:         ceil(0.012 * 2 / 0.04) = ceil(0.60)  = 1
 *   Synthetic:    ceil(0.065 * 2 / 0.04) = ceil(3.25)  = 4
 */
export const DIAGNOSE_AI_COST_SHORT_USD = 0.001;
export const DIAGNOSE_AI_COST_LONG_USD = 0.012;
export const DIAGNOSE_AI_COST_SYNTHETIC_AD_USD = 0.065;
export const PRECISION_TRIM_ANALYSIS_SEED_COST = Math.max(
  1,
  Math.ceil(
    (PRECISION_TRIM_ANALYSIS_AI_COST_USD * TARGET_PROFIT_MULTIPLIER) /
      SEED_PRICE_USD
  )
);

/** Cost in seeds for each feature action (100% profit target: ceil(AI_cost × 2 / $0.04)) */
export const SEED_COSTS = {
  /** Creating a new project / video upload (includes auto-thumbnail) — AI ~$0.25 */
  new_video_upload: 13,
  /** RE-Optimize analysis — AI ~$0.02 */
  reoptimize: 1,
  /** Video Ideas generation — AI ~$0.02 */
  video_ideas: 1,
  /** Channel Description Generator run — AI ~$0.02 */
  channel_description: 1,
  /** Precision Trim export (when approved suggestions are applied) — AI ~$0.02 */
  precision_trim_export: 1,
  /** Mid-roll ad placement analysis (captions-first workflow) — AI ~$0.002 */
  midroll_analysis: 1,
  /** AI thumbnail generation (per image, including edit/scratch flows) — AI ~$0.134 */
  thumbnail: 7,
  /** Video diagnosis — short / flash-lite path (≤15s or non-hybrid) — AI ~$0.001 → 1 seed at 100% margin */
  diagnose: Math.max(
    1,
    Math.ceil(
      (DIAGNOSE_AI_COST_SHORT_USD * TARGET_PROFIT_MULTIPLIER) / SEED_PRICE_USD
    )
  ),
  /** Video diagnosis — long / Pro path (hybrid or >15s, gemini-3.1-pro) — AI ~$0.012 → 1 seed at 100% margin */
  diagnose_long_video: Math.max(
    1,
    Math.ceil(
      (DIAGNOSE_AI_COST_LONG_USD * TARGET_PROFIT_MULTIPLIER) / SEED_PRICE_USD
    )
  ),
  /**
   * Video diagnosis — synthetic ad with native video upload to Gemini 3.1 Pro
   * (Files API). Sends the full video bytes (≤25MB) instead of sparse JPEG
   * frames so the model can actually observe sub-200ms phoneme alignment for
   * the lipSync dimension. Worst-case AI cost ~$0.065 → 4 seeds at 100% margin.
   */
  diagnose_synthetic_ad: Math.max(
    1,
    Math.ceil(
      (DIAGNOSE_AI_COST_SYNTHETIC_AD_USD * TARGET_PROFIT_MULTIPLIER) /
        SEED_PRICE_USD
    )
  ),
  /**
   * Music video diagnosis (BPM, beat sync, energy, lyric alignment + LLM).
   * Similar multimodal cost to synthetic ad (~4 seeds).
   */
  diagnose_music: 4,
  /** Autoclips / segment detection (per analysis) — AI ~$0.05 */
  autoclips: 3,
  /** Autoclips / shorts reframing (one-time per generation batch) — AI ~$0.25 */
  autoclips_reframe: 13,
  /** Video dubbing (per 30-second billing unit) — AI ~$0.297 */
  dubbing_per_30_seconds: 15,
  /**
   * Lip Sync dubbing (per 30-second billing unit) — ElevenLabs + sync.so combined.
   * Breakdown per 30s:
   *   - ElevenLabs: ~$0.297 → 15 seeds
   *   - sync.so lipsync-2 at $0.05/sec × 30s = $1.50 → 38 seeds at 2× margin
   *   - Total: ~53 seeds → rounded to 100 seeds/30s for clean UI + buffer
   */
  lipsync_per_30_seconds: 100,
  /** Multi-language translation (per language) — AI ~$0.008 */
  translation: 1,
  /** Intelligence page full-channel analysis run */
  intelligence_analysis: INTELLIGENCE_ANALYSIS_SEED_COST,
  /** Why page video autopsy analysis */
  why_analysis: WHY_ANALYSIS_SEED_COST,
  /** Precision Trim: one AI analysis (transcription + critique plan) */
  precision_trim_analysis: PRECISION_TRIM_ANALYSIS_SEED_COST,
} as const;

/** Dubbing billing rounds input duration up to 10-second increments. */
export const DUBBING_DURATION_ROUNDING_SECONDS = 10;
/** Dubbing is charged in 30-second billing units. */
export const DUBBING_BILLING_UNIT_SECONDS = 30;

function getSafeDurationSeconds(durationSeconds: number): number {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return DUBBING_BILLING_UNIT_SECONDS;
  }
  return durationSeconds;
}

export function roundUpDubbingDurationSeconds(durationSeconds: number): number {
  const safeDurationSeconds = getSafeDurationSeconds(durationSeconds);
  return (
    Math.ceil(safeDurationSeconds / DUBBING_DURATION_ROUNDING_SECONDS) *
    DUBBING_DURATION_ROUNDING_SECONDS
  );
}

export function getDubbingBillingUnits(durationSeconds: number): number {
  return Math.max(
    1,
    Math.ceil(
      roundUpDubbingDurationSeconds(durationSeconds) /
        DUBBING_BILLING_UNIT_SECONDS
    )
  );
}

export function getDubbingSeedsPerBillingUnit(): number {
  return SEED_COSTS.dubbing_per_30_seconds;
}

export function calculateDubbingSeedCost(durationSeconds: number): number {
  return (
    getDubbingBillingUnits(durationSeconds) * getDubbingSeedsPerBillingUnit()
  );
}

export function getLipsyncSeedsPerBillingUnit(): number {
  return SEED_COSTS.lipsync_per_30_seconds;
}

export function calculateLipsyncSeedCost(durationSeconds: number): number {
  return (
    getDubbingBillingUnits(durationSeconds) * getLipsyncSeedsPerBillingUnit()
  );
}

/** Seeds earned per action */
export const SEED_EARNINGS = {
  video_publish: 1,
  serious_bug_report_confirmed: 100,
} as const;

/** Bonus seeds at streak milestones (day N) */
export const STREAK_BONUSES: Record<number, number> = {
  3: 2,
  7: 3,
  30: 10,
};

/** Starter grant for new users: enough for several first-value actions without unlimited free exploration. */
export const STARTER_GRANT_AMOUNT = 50;

/** One-time bonus for legacy V1 users migrating to V2 */
export const LEGACY_MIGRATION_BONUS = 300;

/** Referral reward amounts */
export const REFERRAL_REWARDS = {
  referrer: 50,
  invitee: 25,
  max_per_month: 20,
} as const;

/**
 * One-time seed packages (buy seeds, never expire).
 * Per-seed pricing: Handful $0.10, Bag $0.09, Truckload $0.08.
 */
export const ONE_TIME_PACKAGES = [
  { name: "Handful", seedAmount: 50, priceCents: 500 },
  { name: "Bag", seedAmount: 200, priceCents: 1800 },
  { name: "Truckload", seedAmount: 500, priceCents: 4000 },
] as const;

export function getOneTimePackagesForDisplay(): Array<{
  id: string;
  name: string;
  seedAmount: number;
  priceInCents: number;
  sortOrder: number;
}> {
  return ONE_TIME_PACKAGES.map((pkg, index) => ({
    id: pkg.name.toLowerCase(),
    name: pkg.name,
    seedAmount: pkg.seedAmount,
    priceInCents: pkg.priceCents,
    sortOrder: index + 1,
  }));
}

/**
 * Monthly seed pack subscriptions (seeds delivered each billing cycle).
 * Prices aligned with V1 plans (same Stripe price IDs for seamless migration):
 *   Sprout ↔ V1 Basic ($8), Growth ↔ V1 Creator ($19),
 *   Harvest ↔ V1 Pro ($49), Agency ↔ V1 Agency ($149).
 * Per-seed pricing: Sprout $0.08, Growth $0.06, Harvest $0.05, Agency $0.05.
 */
export const MONTHLY_PACKS = [
  { key: "sprout", name: "Sprout", seedsPerMonth: 100, priceCents: 800 },
  { key: "growth", name: "Growth", seedsPerMonth: 320, priceCents: 1900 },
  { key: "harvest", name: "Harvest", seedsPerMonth: 980, priceCents: 4900 },
  { key: "agency", name: "Agency", seedsPerMonth: 2980, priceCents: 14900 },
] as const;

/** Display order for Seed Costs section (keys from SEED_COSTS) */
export const SEED_COSTS_DISPLAY_ORDER = [
  "new_video_upload",
  "autoclips",
  "autoclips_reframe",
  "precision_trim_analysis",
  "precision_trim_export",
  "dubbing_per_30_seconds",
  "lipsync_per_30_seconds",
  "diagnose",
  "diagnose_long_video",
  "diagnose_synthetic_ad",
  "reoptimize",
  "video_ideas",
  "channel_description",
  "translation",
  "midroll_analysis",
  "intelligence_analysis",
  "why_analysis",
  "thumbnail",
] as const;

/** Build display array from SEED_COSTS for UI */
export function getSeedCostsForDisplay(): Array<{
  key: string;
  seeds: number;
}> {
  return SEED_COSTS_DISPLAY_ORDER.map((key) => ({
    key: key === "dubbing_per_30_seconds" ? "dubbing" : key,
    seeds: SEED_COSTS[key],
  }));
}
